import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  ClickAwayListener,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import type { PeriodStartSetting } from "../../../../employment/employmentApi";
import { PeriodStartActionButton } from "./styles";

type Props = { settings: readonly PeriodStartSetting[] };

// The parent passes future settings in effective-date order, using the Helsinki reference date.
export const UpcomingPeriodStartChanges = ({ settings }: Props) => {
  const { t } = useTranslation("talentProfile");
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  if (!settings.length) return null;
  const displayDate = (date: string) => format(parseISO(date), "dd.MM.yyyy");
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      <Box component="span" aria-hidden="true" sx={{ color: "text.secondary" }}>
        ·
      </Box>
      <Typography component="span" variant="body2" color="text.secondary">
        {t(
          settings.length === 1
            ? "periodStartNextChange"
            : "periodStartFutureChanges",
          {
            count: settings.length,
            date: displayDate(settings[0].from),
          },
        )}
      </Typography>
      <ClickAwayListener onClickAway={close}>
        <span>
          <InfoTooltip
            open={open}
            onOpen={() => setOpen(true)}
            onClose={close}
            // A tap opens the list until dismissed; no long-press or touch timeout is needed.
            disableTouchListener
            describeChild
            placement="bottom"
            slotProps={{ tooltip: { sx: { maxWidth: 420, p: 1 } } }}
            icon={
              <PeriodStartActionButton
                aria-label={t("periodStartUpcomingDetails")}
                sx={{ my: "-8px" }}
                onClick={() => setOpen(true)}
              >
                <InfoOutlinedIcon />
              </PeriodStartActionButton>
            }
            title={
              <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                <Table
                  size="small"
                  aria-label={t("periodStartUpcomingDetails")}
                  sx={{
                    "& th, & td": {
                      color: "inherit",
                      p: 0.5,
                      borderColor: "rgba(255, 255, 255, 0.25)",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("periodStartEffectiveFrom")}</TableCell>
                      <TableCell>{t("periodStarts")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings.map((setting) => (
                      <TableRow key={setting.from}>
                        <TableCell>{displayDate(setting.from)}</TableCell>
                        <TableCell>
                          {displayDate(setting.periodStart)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            }
          />
        </span>
      </ClickAwayListener>
    </Box>
  );
};
